import { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { marked } from 'marked';

import './translations';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import { chatMarkdownComponents } from './utils/chatTable';
import { getAuthHeaders, appendTokenToUrl, API_BASE_URL, interceptProtectedLinkClick } from './auth';
import { alignNumericColumns } from './utils/markdownTables';

// MUI components
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

// MUI Icons
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import StarIcon from '@mui/icons-material/Star';
import SendIcon from '@mui/icons-material/Send';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MicIcon from '@mui/icons-material/Mic';
import DescriptionIcon from '@mui/icons-material/Description';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

// Shared hooks, components, and utils
import VoiceOverlay from './components/VoiceOverlay';
import AreDisclaimer from './AreDisclaimer';
import useProcessingSound from './hooks/useProcessingSound';
import useAudioPlaybackQueue from './hooks/useAudioPlaybackQueue';
import { computeRMS, encodeWAV, getSupportedMimeType } from './utils/audioUtils';
import CloseIcon from '@mui/icons-material/Close';
import { chatBubbleSx, firstLineSlotSx, modalHeaderBtnSx, InfoTooltip, ModalHeader, ScrollToBottomButton, closeOnlyViaButton, BotAvatar, EmailDraftCard, ATTACHMENT_OPEN_KEYS } from './chatMessageStyles';
import useScrollToBottom from './hooks/useScrollToBottom';

function ExpandableAssistantLion({ userName, title, icon, expanded, sessionId, language, resetSessionId, chatPrefill, onCloseChat, onOpenChat, onResetMessages, modal }) {
  const [vips, setVips] = useState([]);
  const [refreshCount, setRefreshCount] = useState(0);
  // VIP ("very important prompt") save-confirmation dialog + feedback snackbar
  const [vipDialog, setVipDialog] = useState({ open: false, text: '' });
  const [vipSaving, setVipSaving] = useState(false);
  const [vipDeleteDialog, setVipDeleteDialog] = useState({ open: false, vip: null });
  const [vipDeleting, setVipDeleting] = useState(false);
  const [vipSnackbar, setVipSnackbar] = useState({ open: false, severity: 'success', message: '' });
  const [vipEditDialog, setVipEditDialog] = useState({ open: false, vip: null });
  const [vipEditText, setVipEditText] = useState('');
  const [vipEditSaving, setVipEditSaving] = useState(false);
  const [scrollBottom, setScrollBottom] = useState(0);
  const [events, setEvents] = useState([]);
  const { t } = useTranslation();
  const [selectedTab, setSelectedTab] = useState('chat');
  const [messages, setMessages] = useState([{ text: t("chat_welcome_message"), sender: 'bot', isHtml: true }]);
  // The language the welcome bubble was written in. Switching the app language
  // used to re-run a `[t]` effect that replaced `messages` wholesale — which threw
  // away the conversation. A running thread now keeps its language; "Chat leeren"
  // is how you get a greeting in the new one, so the button stays reachable while
  // the welcome is stale (see chatIsEmpty below).
  const [welcomeLang, setWelcomeLang] = useState(language?.language);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const chatContentRef = useRef(null);
  // "Scroll to latest" arrow (don't auto-follow the streaming reply).
  const {
    showScrollDown, bottomSpacerRef, messagesEndRef, handleScroll,
    scrollToBottom, markUserSent, syncOnContentChange,
  } = useScrollToBottom(chatContentRef);
  const today = new Date().toISOString().split('T')[0];
  const [selectedDate, setSelectedDate] = useState(today);
  const textAreaRef = useRef(null);
  const currentBotMessage = useRef('');
  // Set when the backend signals the assistant's next reply IS a customer visit
  // report, so the finalized bot message gets a "download as Word" button.
  const pendingReportRef = useRef(false);
  const [isAudioLoading, setIsAudioLoading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const audioContextRef = useRef(null);
  const mediaStreamSourceRef = useRef(null);
  const processorRef = useRef(null);
  const audioBufferRef = useRef([]);

  // Voice mode states
  const [voiceState, setVoiceState] = useState('idle'); // 'idle' | 'initializing' | 'listening' | 'thinking' | 'speaking'
  // Deliverables (PPT/PDF/report links + email drafts) produced during a voice
  // session. The message list sits BEHIND the opaque VoiceOverlay, so these must
  // also be shown inside the overlay — otherwise the user never sees the file the
  // voice promises "appears below". They persist in `messages` for the chat too.
  const [voiceDeliverables, setVoiceDeliverables] = useState([]);
  const mediaStreamRef = useRef(null);
  const analyserRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const isStartingRef = useRef(false);  // guards against concurrent startListening()
  const audioChunksRef = useRef([]);
  const vadFrameIdRef = useRef(null);
  const speechStartTimeRef = useRef(null);
  const silenceStartTimeRef = useRef(null);
  const voiceStoppedByUserRef = useRef(false);

  // VAD settings
  const VAD_THRESHOLD = 0.025;
  const MIN_SPEECH_MS = 400;
  // 2s (not 1s): a 1s pause cut speakers off mid-sentence and shipped a
  // partial transcript, making the assistant re-ask. See AdvisorVoiceDialog.js.
  const SILENCE_MS = 2000;

  const [audioLevel, setAudioLevel] = useState(0);

  // Use shared processing sound hook
  const { processingPulse } = useProcessingSound(voiceState);

  // Refs for audio playback queue integration
  const currentTranscriptRef = useRef('');
  const botResponseRef = useRef('');
  const startListeningRef = useRef(null);
  const abortControllerRef = useRef(null);
  const consecutiveFailuresRef = useRef(0);
  const MAX_CONSECUTIVE_FAILURES = 3;

  // Use shared audio playback queue hook
  const {
    enqueueAudio,
    markStreamDone,
    stopPlayback,
    // resetQueue is available but not needed here
    audioQueueRef,
    isPlayingRef,
    streamDoneRef,
  } = useAudioPlaybackQueue({
    onQueueEmpty: useCallback(() => {
      console.log('🔄 Queue empty & stream done, transitioning...');
      if (botResponseRef.current) {
        const isReport = pendingReportRef.current;
        pendingReportRef.current = false;
        setMessages(m => [...m, { text: botResponseRef.current, sender: 'bot', isReport }]);
        botResponseRef.current = '';
      }
      if (!voiceStoppedByUserRef.current && startListeningRef.current) {
        console.log('🎤 Restarting listening...');
        startListeningRef.current();
      } else if (voiceStoppedByUserRef.current) {
        console.log('🛑 User stopped voice mode, not restarting listening');
      }
    }, []),
    onPlaying: useCallback(() => {
      setVoiceState('speaking');
    }, []),
  });

  const sendVoiceToBackend = useCallback(async (audioBlob) => {
    // Hard-stop any audio still playing from the previous turn BEFORE starting a
    // new one. Just clearing the queue + resetting isPlaying (as before) left the
    // current source playing, so a new playback chain would start on top of it →
    // two overlapping voices speaking the same content, slightly offset.
    stopPlayback();
    // Abort any in-flight request so we never process two streams at once.
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setVoiceState('thinking');
    setLoading(true);

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    currentTranscriptRef.current = '';
    botResponseRef.current = '';
    streamDoneRef.current = false;

    const mimeToExt = {
      'audio/webm': 'webm',
      'audio/webm;codecs=opus': 'webm',
      'audio/mp4': 'mp4',
      'audio/ogg': 'ogg',
      'audio/ogg;codecs=opus': 'ogg',
      'audio/wav': 'wav',
    };
    const ext = mimeToExt[audioBlob.type] || 'webm';

    const formData = new FormData();
    formData.append('audio', audioBlob, `recording.${ext}`);
    formData.append('session_id', sessionId);
    formData.append('language', language.language);

    try {
      const res = await fetch(`${API_BASE_URL}/api/voice_message`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData,
        signal: abortController.signal
      });

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        if (abortController.signal.aborted) {
          console.log('🛑 Request aborted, stopping stream processing');
          reader.cancel();
          break;
        }

        const { done, value } = await reader.read();
        if (done) {
          // Flush the TextDecoder and process any remaining buffered data
          buffer += decoder.decode();
          if (buffer.trim()) {
            const remainingEvents = buffer.split('\n\n');
            for (const event of remainingEvents) {
              if (!event.trim()) continue;
              let eventType = '';
              let eventData = '';
              for (const line of event.split('\n')) {
                if (line.startsWith('event: ')) eventType = line.slice(7).trim();
                else if (line.startsWith('data: ')) eventData = line.slice(6);
              }
              if (!eventType || !eventData) continue;
              try {
                const data = JSON.parse(eventData);
                console.log(`📨 SSE Event (buffer flush): ${eventType}`, data.audio ? '(audio data)' : data);
                if (eventType === 'artifact' && data.url) {
                  setMessages(m => [...m, { text: artifactMarkdown(data), sender: 'bot' }]);
                  setVoiceDeliverables(d => [...d, { kind: 'artifact', data }]);
                }
                else if (eventType === 'email_draft' && data.mailto) {
                  setMessages(m => [...m, { sender: 'bot', emailDraft: data, text: `E-Mail-Entwurf an ${data.recipient_name || ''}\nBetreff: ${data.subject || ''}\n\n${data.body || ''}` }]);
                  setVoiceDeliverables(d => [...d, { kind: 'email', data }]);
                }
                else if (eventType === 'report_ready') {
                  pendingReportRef.current = true;
                }
                else if (eventType === 'done') {
                  botResponseRef.current = data.full_response || '';
                  markStreamDone();
                  if (audioQueueRef.current.length === 0 && !isPlayingRef.current && data.full_response) {
                    const isReport = pendingReportRef.current;
                    pendingReportRef.current = false;
                    setMessages(m => [...m, { text: data.full_response, sender: 'bot', isReport }]);
                    botResponseRef.current = '';
                  }
                }
              } catch (e) {
                console.warn('Failed to parse remaining SSE data:', eventData, e);
              }
            }
          }
          break;
        }

        buffer += decoder.decode(value, { stream: true });

        const events = buffer.split('\n\n');
        buffer = events.pop() || '';

        for (const event of events) {
          if (abortController.signal.aborted) break;

          if (!event.trim()) continue;

          let eventType = '';
          let eventData = '';

          for (const line of event.split('\n')) {
            if (line.startsWith('event: ')) {
              eventType = line.slice(7).trim();
            } else if (line.startsWith('data: ')) {
              eventData = line.slice(6);
            }
          }

          if (!eventType || !eventData) continue;

          try {
            const data = JSON.parse(eventData);
            console.log(`📨 SSE Event: ${eventType}`, data.audio ? '(audio data)' : data);

            if (eventType === 'transcript' && data.text) {
              currentTranscriptRef.current = data.text;
              setMessages(m => [...m, { text: data.text, sender: 'user' }]);
              consecutiveFailuresRef.current = 0;
            }
            else if (eventType === 'text' && data.chunk) {
              botResponseRef.current += data.chunk;
            }
            else if (eventType === 'artifact' && data.url) {
              // A generated file/link (PowerPoint, PDF, document) — show it as a
              // clickable element BOTH in the chat and inside the voice overlay
              // (which otherwise hides the message list); the voice never reads the URL.
              setMessages(m => [...m, { text: artifactMarkdown(data), sender: 'bot' }]);
              setVoiceDeliverables(d => [...d, { kind: 'artifact', data }]);
            }
            else if (eventType === 'email_draft' && data.mailto) {
              // A drafted email — show a review card with an "E-Mail öffnen" button;
              // the voice only announces it, never reads subject/body/address.
              setMessages(m => [...m, { sender: 'bot', emailDraft: data, text: `E-Mail-Entwurf an ${data.recipient_name || ''}\nBetreff: ${data.subject || ''}\n\n${data.body || ''}` }]);
              setVoiceDeliverables(d => [...d, { kind: 'email', data }]);
            }
            else if (eventType === 'report_ready') {
              // The spoken reply is a customer visit report → mark the resulting
              // chat message so it gets a "download as Word" button.
              pendingReportRef.current = true;
            }
            else if (eventType === 'audio' && data.audio) {
              console.log(`🔊 Audio chunk ${data.index} received, queue length: ${audioQueueRef.current.length}`);
              enqueueAudio(data);
            }
            else if (eventType === 'done') {
              console.log('✅ Stream done, queue length:', audioQueueRef.current.length, 'isPlaying:', isPlayingRef.current);
              botResponseRef.current = data.full_response || '';
              markStreamDone();

              if (data.reason === 'empty_transcript' || (!data.full_response && audioQueueRef.current.length === 0)) {
                consecutiveFailuresRef.current++;
                console.log(`📭 Empty response (failure ${consecutiveFailuresRef.current}/${MAX_CONSECUTIVE_FAILURES})`);
                setLoading(false);

                if (consecutiveFailuresRef.current >= MAX_CONSECUTIVE_FAILURES) {
                  console.log('❌ Too many consecutive failures, stopping voice mode');
                  consecutiveFailuresRef.current = 0;
                  if (mediaStreamRef.current) {
                    mediaStreamRef.current.getTracks().forEach(t => t.stop());
                    mediaStreamRef.current = null;
                  }
                  if (audioContextRef.current) {
                    audioContextRef.current.close().catch(() => {});
                    audioContextRef.current = null;
                  }
                  setVoiceState('idle');
                  return;
                }

                if (startListeningRef.current && !voiceStoppedByUserRef.current) {
                  if (mediaStreamRef.current) {
                    mediaStreamRef.current.getTracks().forEach(t => t.stop());
                    mediaStreamRef.current = null;
                  }
                  if (audioContextRef.current) {
                    audioContextRef.current.close().catch(() => {});
                    audioContextRef.current = null;
                  }
                  setTimeout(() => {
                    if (!voiceStoppedByUserRef.current) {
                      startListeningRef.current();
                    }
                  }, 500);
                }
                return;
              }

              consecutiveFailuresRef.current = 0;

              if (audioQueueRef.current.length === 0 && !isPlayingRef.current) {
                if (data.full_response) {
                  const isReport = pendingReportRef.current;
                  pendingReportRef.current = false;
                  setMessages(m => [...m, { text: data.full_response, sender: 'bot', isReport }]);
                }
                botResponseRef.current = '';
                if (startListeningRef.current) {
                  startListeningRef.current();
                }
              }
            }
            else if (eventType === 'error') {
              console.error('Voice stream error:', data.message);
              setMessages(m => [...m, { text: 'Voice message failed. Please try again.', sender: 'bot' }]);
              setVoiceState('idle');
            }
          } catch (e) {
            console.warn('Failed to parse SSE data:', eventData, e);
          }
        }
      }
      // Safety net: if stream ended but done event was never processed, add the bot message
      if (!streamDoneRef.current && botResponseRef.current && botResponseRef.current.trim()) {
        console.log('⚠️ Safety net: adding bot response that was never confirmed by done event');
        setMessages(m => [...m, { text: botResponseRef.current, sender: 'bot' }]);
        botResponseRef.current = '';
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        console.log('🛑 Request was aborted (barge-in)');
        if (botResponseRef.current && botResponseRef.current.trim()) {
          const partialResponse = botResponseRef.current;
          setMessages(m => [...m, { text: partialResponse, sender: 'bot' }]);
          console.log('📝 Saved partial response:', partialResponse.substring(0, 50) + '...');
        }
        botResponseRef.current = '';
        return;
      }
      console.error('Voice request failed:', err);

      consecutiveFailuresRef.current++;
      console.log(`⚠️ Error occurred (failure ${consecutiveFailuresRef.current}/${MAX_CONSECUTIVE_FAILURES})`);

      if (consecutiveFailuresRef.current >= MAX_CONSECUTIVE_FAILURES) {
        console.log('❌ Too many consecutive failures, stopping voice mode');
        consecutiveFailuresRef.current = 0;
        if (mediaStreamRef.current) {
          mediaStreamRef.current.getTracks().forEach(t => t.stop());
          mediaStreamRef.current = null;
        }
        if (audioContextRef.current) {
          audioContextRef.current.close().catch(() => {});
          audioContextRef.current = null;
        }
        setVoiceState('idle');
        return;
      }

      if (startListeningRef.current && !voiceStoppedByUserRef.current) {
        if (mediaStreamRef.current) {
          mediaStreamRef.current.getTracks().forEach(t => t.stop());
          mediaStreamRef.current = null;
        }
        if (audioContextRef.current) {
          audioContextRef.current.close().catch(() => {});
          audioContextRef.current = null;
        }
        setTimeout(() => {
          if (!voiceStoppedByUserRef.current) {
            startListeningRef.current();
          }
        }, 500);
      } else {
        setVoiceState('idle');
      }
    } finally {
      setLoading(false);
      if (abortControllerRef.current?.signal.aborted === false) {
        abortControllerRef.current = null;
      }
    }
  }, [sessionId, language, enqueueAudio, markStreamDone, audioQueueRef, isPlayingRef, streamDoneRef, stopPlayback]);

  const startListening = useCallback(async () => {
    // Guard against concurrent starts (several restart paths can fire for one
    // response). Without this, two MediaRecorders record the same speech and both
    // send → two responses → two overlapping voices. isStartingRef is set
    // synchronously so a second call during the async getUserMedia bails out.
    if (voiceStoppedByUserRef.current) return;
    if (isStartingRef.current || (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording')) {
      console.log('🎤 startListening ignored: already starting/recording');
      return;
    }
    isStartingRef.current = true;
    try {
      let stream = mediaStreamRef.current;
      let audioCtx = audioContextRef.current;
      let analyser = analyserRef.current;

      if (!stream || stream.getTracks().every(t => t.readyState === 'ended')) {
        console.log('🎤 Creating new media stream');
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        mediaStreamRef.current = stream;

        if (audioCtx) {
          audioCtx.close().catch(() => {});
        }
        audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        audioContextRef.current = audioCtx;

        const source = audioCtx.createMediaStreamSource(stream);
        analyser = audioCtx.createAnalyser();
        analyser.fftSize = 512;
        source.connect(analyser);
        analyserRef.current = analyser;
      } else {
        console.log('🎤 Reusing existing media stream');
        if (audioCtx && audioCtx.state === 'suspended') {
          await audioCtx.resume();
        }
      }

      const mimeType = getSupportedMimeType();
      const recorderOptions = mimeType ? { mimeType } : {};
      const recorder = new MediaRecorder(stream, recorderOptions);
      mediaRecorderRef.current = recorder;
      audioChunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };

      recorder.onstop = () => {
        if (voiceStoppedByUserRef.current) {
          console.log('🛑 Recorder stopped but user closed voice mode, discarding audio');
          audioChunksRef.current = [];
          return;
        }

        if (audioChunksRef.current.length > 0) {
          const actualMimeType = recorder.mimeType || 'audio/webm';
          const blob = new Blob(audioChunksRef.current, { type: actualMimeType });
          audioChunksRef.current = [];

          const speechDuration = speechStartTimeRef.current
            ? Date.now() - speechStartTimeRef.current
            : 0;

          if (blob.size < 3000 || speechDuration < 500) {
            console.log(`⚠️ Audio rejected: size=${blob.size}, speechDuration=${speechDuration}ms`);
            if (startListeningRef.current && !voiceStoppedByUserRef.current) {
              startListeningRef.current();
            }
            return;
          }

          sendVoiceToBackend(blob);
        }
      };

      recorder.start(100);
      isStartingRef.current = false;  // recorder is live; the state guard now applies
      setVoiceState('listening');
      speechStartTimeRef.current = null;
      silenceStartTimeRef.current = null;

      const vadLoop = () => {
        if (!analyserRef.current) return;

        const rms = computeRMS(analyserRef.current);
        setAudioLevel(Math.min(rms * 10, 1));
        const now = Date.now();

        if (rms > VAD_THRESHOLD) {
          silenceStartTimeRef.current = null;
          if (!speechStartTimeRef.current) {
            speechStartTimeRef.current = now;
          }
        } else {
          if (speechStartTimeRef.current && (now - speechStartTimeRef.current) >= MIN_SPEECH_MS) {
            if (!silenceStartTimeRef.current) {
              silenceStartTimeRef.current = now;
            } else if ((now - silenceStartTimeRef.current) >= SILENCE_MS) {
              if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
                mediaRecorderRef.current.stop();
              }
              return;
            }
          }
        }

        vadFrameIdRef.current = requestAnimationFrame(vadLoop);
      };

      vadFrameIdRef.current = requestAnimationFrame(vadLoop);

    } catch (err) {
      console.error('Microphone access denied:', err);
      isStartingRef.current = false;
      setVoiceState('idle');
    }
  }, [sendVoiceToBackend]);

  // Keep ref updated for playNextInQueue to call
  useEffect(() => {
    startListeningRef.current = startListening;
  }, [startListening]);

  // VAD loop for listening state
  useEffect(() => {
    if (voiceState !== 'listening' || !analyserRef.current || !mediaRecorderRef.current) {
      return;
    }

    if (mediaRecorderRef.current.state !== 'recording') {
      return;
    }

    console.log('🎧 Starting VAD loop for listening state');

    const vadLoop = () => {
      if (!analyserRef.current || voiceState !== 'listening') return;

      const rms = computeRMS(analyserRef.current);
      setAudioLevel(Math.min(rms * 10, 1));
      const now = Date.now();

      if (rms > VAD_THRESHOLD) {
        silenceStartTimeRef.current = null;
        if (!speechStartTimeRef.current) {
          speechStartTimeRef.current = now;
        }
      } else {
        if (speechStartTimeRef.current && (now - speechStartTimeRef.current) >= MIN_SPEECH_MS) {
          if (!silenceStartTimeRef.current) {
            silenceStartTimeRef.current = now;
          } else if ((now - silenceStartTimeRef.current) >= SILENCE_MS) {
            console.log('🔇 Silence detected, stopping recorder');
            if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
              mediaRecorderRef.current.stop();
            }
            return;
          }
        }
      }

      vadFrameIdRef.current = requestAnimationFrame(vadLoop);
    };

    vadFrameIdRef.current = requestAnimationFrame(vadLoop);

    return () => {
      if (vadFrameIdRef.current) {
        cancelAnimationFrame(vadFrameIdRef.current);
        vadFrameIdRef.current = null;
      }
    };
  }, [voiceState]);

  const stopVoice = useCallback(() => {
    console.log('🛑 stopVoice called - user closing voice mode');
    voiceStoppedByUserRef.current = true;

    // Save any pending bot response before clearing state
    if (botResponseRef.current && botResponseRef.current.trim()) {
      setMessages(m => [...m, { text: botResponseRef.current, sender: 'bot' }]);
      botResponseRef.current = '';
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    if (vadFrameIdRef.current) {
      cancelAnimationFrame(vadFrameIdRef.current);
      vadFrameIdRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(t => t.stop());
      mediaStreamRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }

    stopPlayback();
    audioChunksRef.current = [];
    isStartingRef.current = false;
    setAudioLevel(0);
    setVoiceState('idle');
  }, [stopPlayback]);

  // Closing the assistant must HARD-CUT any active speech-to-speech session:
  // stop the mic, stop audio playback, abort the in-flight stream — immediately.
  const handleClose = useCallback(() => {
    stopVoice();
    onCloseChat?.();
  }, [stopVoice, onCloseChat]);

  // Belt-and-suspenders: if this component unmounts for ANY reason while voice is
  // active (X button, closing the modal, navigating away), tear the voice session
  // down directly via refs (no setState) so nothing keeps recording or playing.
  useEffect(() => {
    return () => {
      try { abortControllerRef.current?.abort(); } catch (e) { /* noop */ }
      try {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
          mediaRecorderRef.current.stop();
        }
      } catch (e) { /* noop */ }
      try {
        if (mediaStreamRef.current) {
          mediaStreamRef.current.getTracks().forEach(t => t.stop());
          mediaStreamRef.current = null;
        }
      } catch (e) { /* noop */ }
      try {
        if (audioContextRef.current) {
          audioContextRef.current.close().catch(() => {});
          audioContextRef.current = null;
        }
      } catch (e) { /* noop */ }
      try { stopPlayback?.(); } catch (e) { /* noop */ }
      if (vadFrameIdRef.current) {
        cancelAnimationFrame(vadFrameIdRef.current);
        vadFrameIdRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Stop and speak again (barge-in)
  const stopAndSpeakAgain = useCallback(() => {
    console.log('🔄 Stop and speak again - returning to listening mode');

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    stopPlayback();

    speechStartTimeRef.current = null;
    silenceStartTimeRef.current = null;

    startListening();
  }, [startListening, stopPlayback]);

  const toggleVoice = useCallback(async () => {
    if (voiceState === 'idle') {
      voiceStoppedByUserRef.current = false;
      setVoiceDeliverables([]); // fresh session: clear last session's overlay deliverables
      setVoiceState('initializing');

      try {
        const silentAudio = new Audio('data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA');
        silentAudio.volume = 0;
        silentAudio.play().then(() => silentAudio.pause()).catch(() => {});
        console.log('🔓 Audio playback unlock initiated');

        console.log('🎤 Requesting microphone permission during user gesture...');
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        console.log('🎤 Microphone permission granted!');
        mediaStreamRef.current = stream;

        if (audioContextRef.current) {
          audioContextRef.current.close().catch(() => {});
        }
        const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        audioContextRef.current = audioCtx;

        const source = audioCtx.createMediaStreamSource(stream);
        const analyser = audioCtx.createAnalyser();
        analyser.fftSize = 512;
        source.connect(analyser);
        analyserRef.current = analyser;

        startListening();

      } catch (err) {
        console.error('Failed to initialize voice:', err);
        setVoiceState('idle');
      }
    } else {
      stopVoice();
    }
  }, [voiceState, startListening, stopVoice]);

  // Turn a voice-mode artifact ({type,label,url}) into a markdown line that
  // renderMessage() renders as a clickable link (opens in a new tab). Used so the
  // Speech overlay shows the generated file/link without the voice reading the URL.
  const artifactMarkdown = (art) => {
    const icons = { powerpoint: '📊', pdf: '📄', document: '📝', email: '✉️', link: '🔗' };
    // Localised by the UI language — the backend's labels are German.
    const titleKeys = { powerpoint: 'artifact_ready_powerpoint', pdf: 'artifact_ready_pdf', document: 'artifact_ready_document', email: 'artifact_ready_email', link: 'artifact_ready_link' };
    const icon = icons[art.type] || '🔗';
    const title = t(titleKeys[art.type] || 'artifact_ready');
    const label = ATTACHMENT_OPEN_KEYS[art.type] ? t(ATTACHMENT_OPEN_KEYS[art.type]) : t('efd_attachment_open');
    return `${icon} **${title}** — [${label}](${art.url})`;
  };

  const modifyLinks = (htmlText) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlText, 'text/html');

    const links = doc.querySelectorAll('a');
    links.forEach(link => {
      link.setAttribute('target', '_blank');
      link.setAttribute('rel', 'noopener noreferrer');
    });

    return doc.body.innerHTML;
  };

  // theScreener report links ({BASE_URL}/api/thescreener/...) need the login
  // token, which a plain link can't send. One delegated handler on the message
  // list (and on the voice-overlay buttons) takes those clicks over — it also
  // covers links in restored history. Errors surface in the existing snackbar.
  const showFileError = useCallback((message) => {
    setVipSnackbar({ open: true, severity: 'error', message });
  }, []);
  const handleProtectedLinkClick = useCallback((event) => {
    interceptProtectedLinkClick(event, { onError: showFileError });
  }, [showFileError]);

  const renderMessage = (text) => {
    // Force numeric columns right-aligned before the markdown becomes HTML —
    // `marked` only honours an explicit `---:`, and the model omits it often
    // enough that figures ended up flush left (same rule as the Word export).
    const rawHtml = marked.parse(alignNumericColumns(text));
    return modifyLinks(rawHtml);
  };

  const formatDateTime = (dateTimeStr) => {
    const dateTime = new Date(dateTimeStr);
    const date = dateTime.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    const time = dateTime.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', hour12: false });
    return { date, time };
  };

  const fetchEvents = useCallback(async () => {
    setEvents([]);
    setCalendarLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/fetch_calendar_events?date=${selectedDate}`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const data = await response.json();
      setEvents(data.events);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setCalendarLoading(false);
    }
  }, [selectedDate]);

  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
  };

  const handleEventClick = (event) => {
    const { summary, description, start, end } = event;
    const { date, time: startTime } = formatDateTime(start.dateTime);
    const { time: endTime } = formatDateTime(end.dateTime);

    const apiMessage = `Ich habe folgenden Kalendereintrag. Erstelle mir nützliche Informationen, um für den Termin vorbereitet zu sein:
Titel: ${summary}
Beschreibung: ${description}
Datum: ${date}
Start: ${startTime}
Ende: ${endTime}`;

    setSelectedTab('chat');
    setNewMessage(apiMessage);

    setTimeout(() => {
      handleSendMessage();
    }, 0);
  };

  const toggle = () => {
    if (expanded && onCloseChat) {
      stopVoice();  // hard-cut any active speech-to-speech session on close
      onCloseChat();
    } else if (!expanded && onOpenChat) {
      onOpenChat();
    }
  };

  // "Chat leeren" is the ONLY way to lose a conversation: it deletes the stored
  // turns and the session's client context, then starts a new session. Closing
  // the window no longer discards anything (see the restore effect below).
  const resetMessage = () => {
    const doomedSession = sessionId;
    setMessages([
      {
        text: t("chat_welcome_message"),
        sender: 'bot',
        isHtml: true
      }
    ]);
    setWelcomeLang(language?.language);
    setNewMessage('');
    resetSessionId();
    onResetMessages();
    if (textAreaRef.current) {
      textAreaRef.current.style.height = 'auto';
    }
    if (doomedSession) {
      fetch(`${API_BASE_URL}/api/chat_history?session_id=${encodeURIComponent(doomedSession)}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      }).catch((error) => console.warn('Could not clear chat history:', error));
    }
  };

  // Restore the conversation for this session. The turns were always stored
  // server-side but never read back, so reopening the chat (or reloading the
  // page) looked as though everything had been deleted.
  const restoredSessionRef = useRef(null);
  useEffect(() => {
    if (!sessionId || restoredSessionRef.current === sessionId) return;
    restoredSessionRef.current = sessionId;
    let cancelled = false;

    fetch(`${API_BASE_URL}/api/chat_history?session_id=${encodeURIComponent(sessionId)}`, {
      headers: getAuthHeaders(),
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        const restored = data?.messages || [];
        if (cancelled || restored.length === 0) return;
        setMessages([
          { text: t('chat_welcome_message'), sender: 'bot', isHtml: true },
          ...restored.map((m) => ({ text: m.text, sender: m.sender })),
        ]);
        setWelcomeLang(language?.language);
      })
      .catch((error) => console.warn('Could not restore chat history:', error));

    return () => { cancelled = true; };
  }, [sessionId, t, language]);

  // Position the just-sent user message at the top of the viewport, then keep
  // the spacer sized as the reply streams in (no auto-follow). Ported from
  // chatModal — see useScrollToBottom.
  useLayoutEffect(() => {
    syncOnContentChange();
  }, [messages, loading, syncOnContentChange]);

  // Explicit jump to the latest (after loading a VIP prompt into the input, and
  // on first open).
  useLayoutEffect(() => {
    messagesEndRef.current?.scrollIntoView();
  }, [scrollBottom, messagesEndRef]);

  useEffect(() => {
    if (selectedTab === 'calendar' && selectedDate != null) {
      fetchEvents();
    }
  }, [selectedDate, selectedTab, fetchEvents]);

  useEffect(() => {
    const fetchVIPs = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/vip?user_name=${encodeURIComponent(userName)}`, {
          headers: getAuthHeaders(),
        });
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        const data = await response.json();
        setVips(data);
      } catch (error) {
        console.error('Failed to fetch VIPs:', error);
      }
    };

    fetchVIPs();
  }, [refreshCount, userName]);

  useEffect(() => {
    if (expanded && chatPrefill) {
      setNewMessage(chatPrefill);
    }
  }, [expanded, chatPrefill]);

  // Open the confirmation dialog (prefilled with the message text) instead of
  // silently POSTing — the user gets a review step + clear feedback.
  const openVipDialog = (message) => {
    setVipDialog({ open: true, text: message.text || '' });
  };

  const closeVipDialog = () => {
    if (vipSaving) return; // don't allow closing mid-save
    setVipDialog((d) => ({ ...d, open: false }));
  };

  const confirmCreateVIP = async () => {
    const prompt = vipDialog.text.trim();
    if (!prompt) return;

    setVipSaving(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/vip`, {
        method: 'POST',
        headers: getAuthHeaders({
          'Content-Type': 'application/json'
        }),
        body: JSON.stringify({ user_name: userName, prompt })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      await response.json();
      setRefreshCount((current) => current + 1);
      setVipDialog({ open: false, text: '' });
      setVipSnackbar({
        open: true,
        severity: 'success',
        message: t('vip_saved_success', { defaultValue: 'Als VIP gespeichert.' }),
      });
    } catch (error) {
      console.error('Error posting VIP:', error);
      setVipSnackbar({
        open: true,
        severity: 'error',
        message: t('vip_saved_error', { defaultValue: 'Speichern fehlgeschlagen. Bitte erneut versuchen.' }),
      });
    } finally {
      setVipSaving(false);
    }
  };

  // Open the delete-confirmation dialog instead of deleting silently.
  const openDeleteVipDialog = (vip) => {
    setVipDeleteDialog({ open: true, vip });
  };

  const closeDeleteVipDialog = () => {
    if (vipDeleting) return; // don't allow closing mid-delete
    setVipDeleteDialog((d) => ({ ...d, open: false }));
  };

  const confirmDeleteVip = async () => {
    const vip = vipDeleteDialog.vip;
    if (!vip) return;

    setVipDeleting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/vip/${vip.id}`, { method: 'DELETE', headers: getAuthHeaders() });
      if (!response.ok) {
        throw new Error('Failed to delete VIP');
      }
      setVips((currentVips) => currentVips.filter((v) => v.id !== vip.id));
      setRefreshCount((current) => current + 1);
      setVipDeleteDialog({ open: false, vip: null });
      setVipSnackbar({
        open: true,
        severity: 'success',
        message: t('vip_deleted_success', { defaultValue: 'VIP gelöscht.' }),
      });
    } catch (error) {
      console.error('Error deleting VIP:', error);
      setVipSnackbar({
        open: true,
        severity: 'error',
        message: t('vip_deleted_error', { defaultValue: 'Löschen fehlgeschlagen. Bitte erneut versuchen.' }),
      });
    } finally {
      setVipDeleting(false);
    }
  };

  const openEditVipDialog = (vip) => {
    setVipEditText(vip.prompt || '');
    setVipEditDialog({ open: true, vip });
  };

  const closeEditVipDialog = () => {
    if (vipEditSaving) return; // don't allow closing mid-save
    setVipEditDialog({ open: false, vip: null });
  };

  const confirmEditVip = async () => {
    const vip = vipEditDialog.vip;
    if (!vip) return;
    const prompt = vipEditText.trim();
    if (!prompt) return;

    setVipEditSaving(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/vip/${vip.id}`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt }),
      });
      if (!response.ok) {
        throw new Error('Failed to update VIP');
      }
      setVips((currentVips) => currentVips.map((v) => (v.id === vip.id ? { ...v, prompt } : v)));
      setVipEditDialog({ open: false, vip: null });
      setVipSnackbar({
        open: true,
        severity: 'success',
        message: t('vip_updated_success', { defaultValue: 'VIP aktualisiert.' }),
      });
    } catch (error) {
      console.error('Error updating VIP:', error);
      setVipSnackbar({
        open: true,
        severity: 'error',
        message: t('vip_updated_error', { defaultValue: 'Aktualisieren fehlgeschlagen. Bitte erneut versuchen.' }),
      });
    } finally {
      setVipEditSaving(false);
    }
  };

  const handleEventClickVip = (message) => {
    setSelectedTab('chat');
    setNewMessage(message);
    setScrollBottom((current) => current + 1);
    // Focus the input (cursor at end) once the chat tab has rendered, so the
    // user can just press Enter to send. The textarea only exists while the chat
    // tab is active, so we defer past this render's tab switch with setTimeout.
    setTimeout(() => {
      const el = textAreaRef.current;
      if (!el) return;
      el.focus();
      const end = el.value.length;
      el.setSelectionRange(end, end);
    }, 0);
  };

  // Put the cursor back into the input once an answer is complete, so the next
  // question can be typed straight away. Deferred by a tick so it lands after
  // the re-render that re-enables the field; preventScroll keeps the chat where
  // it is (focusing would pull the input into view and undo the "user message
  // at the top" positioning).
  const focusInput = useCallback(() => {
    setTimeout(() => textAreaRef.current?.focus({ preventScroll: true }), 0);
  }, []);

  const generateWordDocument = async (messages, downloadName = 'ChatHistory.docx') => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/generate_word_document`, {
        method: 'POST',
        headers: getAuthHeaders({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({ messages }),
      });

      if (!response.ok) {
        throw new Error('Failed to generate document');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = downloadName;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (error) {
      console.error('Error generating document:', error);
    }
  };

  const handleInput = (e) => {
    setNewMessage(e.target.value);

    const textarea = textAreaRef.current;
    if (!textarea) return;

    textarea.style.height = 'auto';

    let newHeight = textarea.scrollHeight;

    if (newHeight <= 28) {
      newHeight = 28;
      textarea.style.overflowY = 'hidden';
    } else if (newHeight > 150) {
      newHeight = 150;
      textarea.style.overflowY = 'auto';
    } else {
      textarea.style.overflowY = 'hidden';
    }
    textarea.style.height = newHeight + 'px';
  };

  const handleSendMessage = async () => {
    if (newMessage.trim()) {
      if (textAreaRef.current) {
        textAreaRef.current.style.height = 'auto';
      }
      const userMessage = { text: newMessage, sender: 'user' };
      markUserSent(); // slide this user message up to the top; reply streams below
      setMessages(prevMessages => [...prevMessages, userMessage]);
      const messageToSend = newMessage;
      setNewMessage('');
      setLoading(true);
      currentBotMessage.current = "";

      try {
        const eventSource = new EventSource(
          appendTokenToUrl(`/api/chat?user_input=${encodeURIComponent(messageToSend)}&session_id=${sessionId}&language=${language.language}`)
        );

        eventSource.onmessage = (event) => {
          const chunk = event.data;
          if (chunk !== 'end of response') {
            setLoading(false);
            const parsedChunk = chunk.replace(/<newline>/g, '\n');
            currentBotMessage.current += parsedChunk;
            setMessages(messages => {
              const lastMessage = messages[messages.length - 1];
              // Never merge streamed text into an email-draft card — the model's
              // confirmation goes into its own message so the card is preserved.
              if (lastMessage && lastMessage.sender === 'bot' && !lastMessage.emailDraft) {
                return [...messages.slice(0, -1), { ...lastMessage, text: currentBotMessage.current }];
              } else {
                return [...messages, { text: currentBotMessage.current, sender: 'bot' }];
              }
            });
          } else {
            eventSource.close();
          }
        };

        eventSource.onerror = (error) => {
          console.error('Error fetching response:', error);
          setMessages(prevMessages => [
            ...prevMessages,
            { text: 'Failed to get response from server.', sender: 'bot' }
          ]);
          setLoading(false);
          focusInput();
          eventSource.close();
        };

        eventSource.addEventListener('email_draft', (event) => {
          // Drafted email → render a review card with an "E-Mail öffnen" button,
          // identical to how it appears in Speech mode.
          try {
            const draft = JSON.parse(event.data);
            if (draft && draft.mailto) {
              setMessages(m => [...m, { sender: 'bot', emailDraft: draft, text: `E-Mail-Entwurf an ${draft.recipient_name || ''}\nBetreff: ${draft.subject || ''}\n\n${draft.body || ''}` }]);
              // Start the model's confirmation as a fresh message after the card.
              currentBotMessage.current = "";
            }
          } catch (e) {
            console.warn('Failed to parse email_draft event:', e);
          }
        });

        eventSource.addEventListener('report_ready', () => {
          // The assistant's upcoming reply is a customer visit report.
          pendingReportRef.current = true;
        });

        eventSource.addEventListener('end', () => {
          const isReport = pendingReportRef.current;
          pendingReportRef.current = false;
          setMessages(messages => {
            const lastMessage = messages[messages.length - 1];
            // Finalize the streamed text into a plain bot message — but never
            // overwrite an email-draft card. If the last message is a card (or
            // there's no bot text), only append when we actually have text.
            if (lastMessage && lastMessage.sender === 'bot' && !lastMessage.emailDraft) {
              return [...messages.slice(0, -1), { ...lastMessage, text: currentBotMessage.current, isReport: isReport || lastMessage.isReport }];
            } else if (currentBotMessage.current) {
              return [...messages, { text: currentBotMessage.current, sender: 'bot', isReport }];
            }
            return messages;
          });
          setLoading(false);
          focusInput();   // answer complete → ready for the next question
          eventSource.close();
        });

      } catch (error) {
        console.error('Error fetching response:', error);
        setMessages(prevMessages => [
          ...prevMessages,
          { text: 'Failed to get response from server.', sender: 'bot' }
        ]);
        setLoading(false);
        focusInput();
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !loading) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleRecordClick = async () => {
    if (isRecording) {
      // Capture the actual capture rate before closing the context; the WAV
      // header must match the native rate the ScriptProcessor recorded at.
      const sampleRate = audioContextRef.current ? audioContextRef.current.sampleRate : 44100;

      if (processorRef.current && mediaStreamSourceRef.current) {
        mediaStreamSourceRef.current.disconnect();
        processorRef.current.disconnect();
      }

      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
      }
      if (mediaStreamSourceRef.current && mediaStreamSourceRef.current.mediaStream) {
        mediaStreamSourceRef.current.mediaStream.getTracks().forEach(track => track.stop());
      }

      setIsRecording(false);

      processAudioData(audioBufferRef.current, sampleRate);
    } else {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        alert('Your browser does not support audio recording.');
        return;
      }

      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        audioContextRef.current = audioContext;
        mediaStreamSourceRef.current = audioContext.createMediaStreamSource(stream);
        processorRef.current = audioContext.createScriptProcessor(4096, 1, 1);

        mediaStreamSourceRef.current.connect(processorRef.current);
        processorRef.current.connect(audioContext.destination);

        audioBufferRef.current = [];

        processorRef.current.onaudioprocess = (e) => {
          const inputBuffer = e.inputBuffer.getChannelData(0);
          audioBufferRef.current.push(new Float32Array(inputBuffer));
        };

        setIsRecording(true);
      } catch (error) {
        console.error('Error accessing microphone:', error);
        alert('Could not access your microphone. Please check permissions.');
      }
    }
  };

  const processAudioData = async (buffer, sampleRate = 44100) => {
    setIsAudioLoading(true);

    const wavBlob = encodeWAV(buffer, sampleRate);
    const reader = new FileReader();
    reader.readAsDataURL(wavBlob);

    reader.onloadend = async () => {
      const base64data = reader.result.split(',')[1];

      try {
        const response = await fetch(`${API_BASE_URL}/api/translate_audio_to_text`, {
          method: 'POST',
          headers: getAuthHeaders({
            'Content-Type': 'application/json',
          }),
          body: JSON.stringify({ audio_file: base64data }),
        });

        if (!response.ok) {
          throw new Error('Failed to process audio');
        }

        const data = await response.json();
        if (data && data.user_input) {
          setNewMessage(data.user_input);
        } else {
          console.error('Invalid response from API');
        }
      } catch (error) {
        console.error('Error processing audio:', error);
      } finally {
        setIsAudioLoading(false);
      }
    };
  };

  // Nothing to clear only when the transcript is still just the welcome AND that
  // welcome is already in the language the user is reading.
  const chatIsEmpty = messages.length <= 1 && welcomeLang === language?.language;

  const tabValue = selectedTab === 'chat' ? 0 : selectedTab === 'calendar' ? 1 : 2;

  const handleTabChange = (event, newValue) => {
    const tabMap = ['chat', 'calendar', 'vip'];
    setSelectedTab(tabMap[newValue]);
  };

  return (
    <>
    <Accordion
      expanded={modal ? true : expanded}
      onChange={modal ? undefined : toggle}
      sx={modal
        ? {
            m: 0,
            background: 'transparent',
            border: 'none',
            boxShadow: 'none',
            backdropFilter: 'none',
            borderRadius: 0,
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            '&:before': { display: 'none' },
            '&:hover': { background: 'transparent', borderColor: 'transparent', boxShadow: 'none' },
            '&.Mui-expanded': { background: 'transparent', border: 'none', boxShadow: 'none', margin: 0 },
            // Let the chat fill the modal height: stretch MUI's Collapse wrappers.
            '& .MuiCollapse-root': { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' },
            '& .MuiCollapse-wrapper': { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' },
            '& .MuiCollapse-wrapperInner': { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' },
            '& .MuiAccordion-region': { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' },
          }
        : { mb: 2 }}
    >
      <AccordionSummary
        expandIcon={modal ? null : <ExpandMoreIcon sx={{ color: 'text.secondary' }} />}
        sx={{
          '& .MuiAccordionSummary-content': {
            alignItems: 'center',
            gap: 1.5,
            // In the modal: no vertical margins (the Advisor Reasoning Engine
            // header has none either) — just an 8px padding-bottom on the row.
            my: modal ? 0 : '10px',
            '&.Mui-expanded': { my: modal ? 0 : '10px' },
          },
          // In the modal, drop the forced 48px min-height + horizontal padding
          // so the header (icon / title / Clear Chat / Close) matches the ARE
          // header 1:1: flush at the content edge, natural height, pb 8px only.
          minHeight: modal ? 'auto' : 48,
          '&.Mui-expanded': { minHeight: modal ? 'auto' : 48 },
          px: modal ? 0 : 1.5,
          ...(modal && { pt: 0, pb: 1, cursor: 'default' }),
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 32,
            height: 32,
            borderRadius: '8px',
            bgcolor: 'rgba(25, 208, 239, 0.08)',
            color: 'primary.main',
            '& .MuiSvgIcon-root': { fontSize: 18 },
          }}
        >
          {icon}
        </Box>
        <Typography sx={{ fontWeight: 400, fontSize: '15px', letterSpacing: '0.02em', flex: 1 }}>
          {title}
        </Typography>
        {modal && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} onClick={(e) => e.stopPropagation()}>
            {selectedTab === 'chat' && (
              <InfoTooltip title={chatIsEmpty ? t('no_chat_history') : ''}>
                <span>
                  <Button
                    onClick={resetMessage}
                    disabled={loading || isAudioLoading || voiceState !== 'idle' || chatIsEmpty}
                    size="small"
                    startIcon={<DeleteIcon />}
                    sx={{ ...modalHeaderBtnSx, px: 1.5, py: 0.5, fontSize: '13px', fontWeight: 600 }}
                  >
                    {t('clear_chat')}
                  </Button>
                </span>
              </InfoTooltip>
            )}
            <IconButton
              onClick={handleClose}
              size="small"
              aria-label={t('close', { defaultValue: 'Close' })}
              sx={{ ...modalHeaderBtnSx, p: 0.75 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </AccordionSummary>
      <AccordionDetails sx={{ position: 'relative', pt: 0, px: modal ? 0 : 1.5, pb: modal ? 0 : 1.5, display: 'flex', flexDirection: 'column', overflow: 'hidden', ...(modal && { flex: 1, minHeight: 0 }) }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="fullWidth"

          sx={{
            minHeight: 40,
            // Thin continuous line under all tabs; the selected tab's brighter,
            // thicker indicator sits on top — so it's clear there are multiple tabs.
            borderBottom: '1px solid rgba(25, 208, 239, 0.35)',
            '& .MuiTab-root': {
              minHeight: 40,
              textTransform: 'none',
              fontSize: 'var(--el-font-size-button)',
              color: 'text.secondary',
              '&.Mui-selected': {
                color: 'primary.main',
              },
            },
          }}
        >
          <Tab label={t('chat-tab')} />
          <Tab label={t('calendar-tab')} />
          <Tab label={t('very-important-prompts')} />
        </Tabs>
        <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {selectedTab === 'chat' && (
            <>
              <Box

                sx={{ position: 'relative', flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
              >
                <Box
                  ref={chatContentRef}
                  onScroll={handleScroll}
                  onClick={handleProtectedLinkClick}
                  onAuxClick={handleProtectedLinkClick}
                  sx={{ position: 'relative', flex: 1, overflowY: 'auto', mt: '14px', pr: 1.5 }}
                >
                  {messages.map((message, index) => (
                    <Box
                      key={index}
                      className={message.sender === 'user' ? 'chat-user-msg' : undefined}
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        flexWrap: 'wrap',
                        gap: 1,
                        mb: 1,
                        flexDirection: message.sender === 'user' ? 'row-reverse' : 'row',
                      }}
                    >
                      {message.sender === 'bot' && <BotAvatar />}
                      <Box sx={chatBubbleSx(message.sender === 'user')}>
                        {message.emailDraft ? (
                          <EmailDraftCard draft={message.emailDraft} openLabel={t('open_email')} />
                        ) : (
                          <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            rehypePlugins={[rehypeRaw]}
                            components={chatMarkdownComponents}
                          >
                            {renderMessage(message.text)}
                          </ReactMarkdown>
                        )}
                        {message.isReport && (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<DescriptionIcon fontSize="small" />}
                            onClick={() => generateWordDocument([message], 'ClientVisitReport.docx')}
                            sx={{ mt: 1, textTransform: 'none' }}
                          >
                            {t('download_visit_report')}
                          </Button>
                        )}
                        {index !== 0 && message.sender === 'bot' && (
                          <Box
                            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}
                          >
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <IconButton size="small" sx={{ color: 'text.secondary' }}>
                                <ThumbUpIcon fontSize="small" />
                              </IconButton>
                              <IconButton size="small" sx={{ color: 'text.secondary' }}>
                                <ThumbDownIcon fontSize="small" />
                              </IconButton>
                            </Box>
                            <IconButton
                              size="small"
                              onClick={() => generateWordDocument(messages.slice(1))}
                              sx={{ color: 'text.secondary' }}
                            >
                              <DescriptionIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                      </Box>
                      {message.sender === 'user' && (
                        <Box sx={firstLineSlotSx}>
                          <InfoTooltip title={t('vip_tooltip', { defaultValue: 'Diese Frage als VIP-Prompt speichern' })}>
                            <IconButton
                              size="small"
                              onClick={() => openVipDialog(message)}
                              sx={{ color: 'primary.main', p: 0.5 }}
                            >
                              <StarIcon fontSize="small" />
                            </IconButton>
                          </InfoTooltip>
                        </Box>
                      )}
                    </Box>
                  ))}
                  {loading && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1 }}>
                      <CircularProgress size={20} sx={{ color: 'primary.main' }} />
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {t('load-response')}
                      </Typography>
                    </Box>
                  )}
                  {/* Growable spacer + scroll anchor: let the just-sent user
                      message be scrolled to the top of the viewport. */}
                  <div ref={bottomSpacerRef} style={{ flexShrink: 0 }} />
                  <div ref={messagesEndRef} />
                </Box>

                {/* "Scroll to latest" arrow — shown when not at the bottom. */}
                <ScrollToBottomButton show={showScrollDown} onClick={scrollToBottom} />
              </Box>
              <Box sx={{ pt: 1, borderTop: 1, borderColor: 'divider' }}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                >
                  <TextField
                    inputRef={textAreaRef}
                    value={newMessage}
                    onChange={handleInput}
                    onKeyPress={handleKeyPress}
                    placeholder={isAudioLoading ? "" : t('ask_question')}
                    disabled={loading || isAudioLoading || voiceState !== 'idle'}
                    multiline
                    maxRows={5}
                    variant="outlined"
                    size="small"
                    fullWidth
                    InputProps={{
                      startAdornment: isAudioLoading ? (
                        <InputAdornment position="start">
                          <CircularProgress size={18} sx={{ color: 'primary.main' }} />
                        </InputAdornment>
                      ) : null,
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'background.paper',
                        fontSize: 'var(--el-font-size-button)',
                      },
                      // Keep the focus outline thin (1px) instead of MUI's default 2px.
                      '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderWidth: 1 },
                    }}
                  />
                  <InfoTooltip title={t('send_tooltip', { defaultValue: 'Nachricht senden' })}>
                    <span style={{ display: 'inline-flex' }}>
                      <IconButton
                        onClick={handleSendMessage}
                        disabled={loading || isAudioLoading || voiceState !== 'idle' || !newMessage.trim()}
                        size="small"
                        sx={{ color: 'primary.main' }}
                      >
                        <SendIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </InfoTooltip>
                  <InfoTooltip
                    title={
                      isRecording
                        ? t('stt_stop_tooltip', { defaultValue: 'Aufnahme stoppen' })
                        : t('stt_tooltip', { defaultValue: 'Spracheingabe – Sprache zu Text (Speech-to-Text)' })
                    }
                  >
                    <span style={{ display: 'inline-flex' }}>
                      <IconButton
                        onClick={handleRecordClick}
                        disabled={loading || isAudioLoading || voiceState !== 'idle'}
                        size="small"
                        sx={{ color: isRecording ? 'error.main' : 'text.secondary' }}
                      >
                        {isRecording ? (
                          <FiberManualRecordIcon fontSize="small" />
                        ) : (
                          <MicIcon fontSize="small" />
                        )}
                      </IconButton>
                    </span>
                  </InfoTooltip>
                  <InfoTooltip
                    title={
                      voiceState !== 'idle'
                        ? t('voice_stop_tooltip', { defaultValue: 'Sprachmodus beenden' })
                        : t('voice_tooltip', { defaultValue: 'KI-Sprachagent starten (Live-Gespräch)' })
                    }
                  >
                    <span style={{ display: 'inline-flex' }}>
                      <IconButton
                        onClick={toggleVoice}
                        disabled={loading || isAudioLoading}
                        aria-label="AI Voice Agent"
                        size="small"
                        sx={{
                          color: voiceState !== 'idle' ? 'primary.main' : 'text.secondary',
                        }}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 3V21M8 8V16M4 10V14M16 8V16M20 10V14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                      </IconButton>
                    </span>
                  </InfoTooltip>
                </Box>
              </Box>
            </>
          )}
          {selectedTab === 'calendar' && (
            <Box sx={{ pt: 2 }}>
              <Typography
                component="label"
                htmlFor="dateInput"
                sx={{
                  fontWeight: 600,
                  mb: 1,
                  display: 'block',
                  fontSize: 'var(--el-font-size-button)',
                  color: 'text.primary',
                }}
              >
                {t('date')}
              </Typography>
              <input
                type="date"
                id="dateInput"
                value={selectedDate}
                onChange={handleDateChange}
                style={{
                  background: 'rgba(255, 255, 255, 0.06)',
                  border: '1px solid rgba(255, 255, 255, 0.12)',
                  borderRadius: '8px',
                  color: 'inherit',
                  padding: '8px 12px',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  colorScheme: 'dark',
                }}
              />
              {calendarLoading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                  <CircularProgress size={20} sx={{ color: 'primary.main' }} />
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {t('load-calendar')}
                  </Typography>
                </Box>
              )}
              {events.length > 0 ? (
                <Box sx={{ mt: 2 }}>
                  {events.map((event, index) => {
                    const { time: startTime } = formatDateTime(event.start.dateTime);
                    const { time: endTime } = formatDateTime(event.end.dateTime);
                    return (
                      <Box
                        key={index}
                        onClick={() => handleEventClick(event)}
                        sx={{
                          p: 1.5,
                          mb: 1,
                          borderRadius: 1,
                          bgcolor: 'rgba(255, 255, 255, 0.06)',
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' },
                          border: 1,
                          borderColor: 'divider',
                        }}
                      >
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {startTime} - {endTime} Uhr
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                          {event.summary}
                        </Typography>
                        <Box sx={{ mt: 0.5 }}>
                          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                            {t('description')}
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                            {event.description}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                !calendarLoading && (
                  <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                    {t('no-events')}
                  </Typography>
                )
              )}
            </Box>
          )}
          {selectedTab === 'vip' && (
            <Box sx={{ pt: 2, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              {vips.length > 0 ? (
                <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pr: 0.5 }}>
                  {vips.map((vip, index) => (
                    <Box
                      key={vip.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        p: 1.5,
                        mb: 1,
                        borderRadius: 1,
                        bgcolor: 'rgba(255, 255, 255, 0.06)',
                        border: 1,
                        borderColor: 'divider',
                      }}
                    >
                      <Box
                        onClick={() => handleEventClickVip(vip.prompt)}
                        sx={{ flex: 1, cursor: 'pointer' }}
                      >
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                          #{index + 1}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.primary' }}>
                          {vip.prompt}
                        </Typography>
                      </Box>
                      <IconButton
                        onClick={() => openEditVipDialog(vip)}
                        size="small"
                        sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                      >
                        <EditIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                      <IconButton
                        onClick={() => openDeleteVipDialog(vip)}
                        size="small"
                        sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 17 }} />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Keine gespeicherten Prompts
                </Typography>
              )}
            </Box>
          )}
        </Box>
        {/* Persistent AI disclaimer — same component + styling as the Advisor
           Reasoning Engine, pinned to the bottom under all tabs. */}
        <AreDisclaimer textKey="assistant_disclaimer" sx={{ py: 0, pt: { xs: 1.5, md: 2 } }} />

        {/* Voice overlay — mounted at the modal-body level so it fully covers the
           tabs, chat and input row behind it (nothing shows through), matching
           how the ARE voice overlay covers its whole modal. */}
        <VoiceOverlay
          voiceState={voiceState}
          audioLevel={audioLevel}
          processingPulse={processingPulse}
          onStop={stopVoice}
          onBargeIn={stopAndSpeakAgain}
        >
          {voiceDeliverables.map((d, i) => (
            <Box key={i} sx={{ width: '100%', maxWidth: 460 }} onClick={(e) => e.stopPropagation()}>
              {d.kind === 'email'
                ? <EmailDraftCard draft={d.data} openLabel={t('open_email')} />
                : (
                  <Button
                    variant="contained"
                    component="a"
                    href={d.data.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleProtectedLinkClick}
                    onAuxClick={handleProtectedLinkClick}
                    sx={{
                      textTransform: 'none', fontWeight: 700, width: '100%',
                      color: '#0a0a2e', WebkitTextFillColor: '#0a0a2e',
                      backgroundColor: 'primary.main',
                      '&:hover': { backgroundColor: 'primary.dark', color: '#0a0a2e', WebkitTextFillColor: '#0a0a2e' },
                    }}
                  >
                    {(d.data.type === 'powerpoint' ? '📊 ' : d.data.type === 'pdf' ? '📄 ' : d.data.type === 'document' ? '📝 ' : '🔗 ')}
                    {d.data.label || 'Öffnen'}
                  </Button>
                )}
            </Box>
          ))}
        </VoiceOverlay>
      </AccordionDetails>
    </Accordion>

      {/* VIP save-confirmation dialog — review the prompt, then confirm. */}
      <Dialog
        open={vipDialog.open}
        onClose={closeOnlyViaButton(closeVipDialog)}
        maxWidth="xs"
        fullWidth
      >
        <Box sx={{ px: 3, pt: 2 }}>
          <ModalHeader icon={<StarIcon />} title={t('vip_save_title', { defaultValue: 'Als VIP speichern' })} onClose={closeVipDialog} />
        </Box>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            {t('vip_save_desc', { defaultValue: 'Diesen Prompt als „Very Important Prompt" speichern, um ihn später schnell wiederzuverwenden.' })}
          </Typography>
          <TextField
            value={vipDialog.text}
            onChange={(e) => setVipDialog((d) => ({ ...d, text: e.target.value }))}
            label={t('vip_prompt_label', { defaultValue: 'Prompt' })}
            multiline
            minRows={2}
            maxRows={6}
            fullWidth
            autoFocus
            disabled={vipSaving}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeVipDialog} color="inherit" disabled={vipSaving}>
            {t('vip_cancel', { defaultValue: 'Abbrechen' })}
          </Button>
          <Button
            onClick={confirmCreateVIP}
            variant="contained"
            disabled={vipSaving || !vipDialog.text.trim()}
            startIcon={vipSaving ? <CircularProgress size={16} color="inherit" /> : <StarIcon />}
          >
            {t('vip_save_confirm', { defaultValue: 'Speichern' })}
          </Button>
        </DialogActions>
      </Dialog>

      {/* VIP delete-confirmation dialog — same styling as the save dialog. */}
      <Dialog
        open={vipDeleteDialog.open}
        onClose={closeOnlyViaButton(closeDeleteVipDialog)}
        maxWidth="xs"
        fullWidth
      >
        <Box sx={{ px: 3, pt: 2 }}>
          <ModalHeader icon={<DeleteIcon />} title={t('vip_delete_title', { defaultValue: 'VIP löschen' })} onClose={closeDeleteVipDialog} />
        </Box>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: vipDeleteDialog.vip?.prompt ? 2 : 0 }}>
            {t('vip_delete_desc', { defaultValue: 'Diesen gespeicherten Prompt wirklich löschen? Das kann nicht rückgängig gemacht werden.' })}
          </Typography>
          {vipDeleteDialog.vip?.prompt && (
            <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: 'rgba(255, 255, 255, 0.06)', border: 1, borderColor: 'divider', maxHeight: 120, overflow: 'auto' }}>
              <Typography variant="body2" sx={{ color: 'text.primary' }}>
                {vipDeleteDialog.vip.prompt}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDeleteVipDialog} color="inherit" disabled={vipDeleting}>
            {t('vip_cancel', { defaultValue: 'Abbrechen' })}
          </Button>
          <Button
            onClick={confirmDeleteVip}
            variant="contained"
            color="error"
            disabled={vipDeleting}
            startIcon={vipDeleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
          >
            {t('vip_delete_confirm', { defaultValue: 'Löschen' })}
          </Button>
        </DialogActions>
      </Dialog>

      {/* VIP edit dialog — same modal styling as the customer-notes edit dialog. */}
      <Dialog
        open={vipEditDialog.open}
        onClose={closeOnlyViaButton(closeEditVipDialog)}
        maxWidth="sm"
        fullWidth
        sx={{ '& .MuiDialog-container': { alignItems: 'center', justifyContent: 'center' } }}
        PaperProps={{ sx: { borderRadius: '14px' } }}
      >
        <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography sx={{ fontWeight: 600, fontSize: '16px' }}>{t('vip_edit_title', { defaultValue: 'VIP bearbeiten' })}</Typography>
            <IconButton onClick={closeEditVipDialog} size="small" sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <TextField
            value={vipEditText}
            onChange={(e) => setVipEditText(e.target.value)}
            label={t('vip_prompt_label', { defaultValue: 'Prompt' })}
            multiline
            minRows={3}
            maxRows={8}
            fullWidth
            autoFocus
            disabled={vipEditSaving}
          />
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 0.5 }}>
            <Button onClick={closeEditVipDialog} disabled={vipEditSaving} sx={{ textTransform: 'none' }}>
              {t('vip_cancel', { defaultValue: 'Abbrechen' })}
            </Button>
            <Button
              variant="contained"
              onClick={confirmEditVip}
              disabled={!vipEditText.trim() || vipEditSaving}
              startIcon={vipEditSaving ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {t('vip_save_confirm', { defaultValue: 'Speichern' })}
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* Feedback after saving/deleting (or on error), also used for report
         links that could not be opened. Errors stay a bit longer — they are
         full sentences the user has to read. */}
      <Snackbar
        open={vipSnackbar.open}
        autoHideDuration={vipSnackbar.severity === 'error' ? 6000 : 3000}
        onClose={() => setVipSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setVipSnackbar((s) => ({ ...s, open: false }))}
          severity={vipSnackbar.severity}
          variant="filled"
          sx={{
            width: '100%',
            // Use the project's cyan for success instead of MUI's default green.
            ...(vipSnackbar.severity === 'success' && {
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              '& .MuiAlert-icon': { color: 'primary.contrastText' },
            }),
          }}
        >
          {vipSnackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default ExpandableAssistantLion;
